import { TelemetryClient } from '../clients/TelemetryClient';
import { CommonConstants } from '@shared/common';
import type { TelemetryParams } from '../clients/TelemetryClient';

/** GA4 event names emitted by the semantic telemetry API. */
export const TelemetryEvents = {
  AppSessionStart: 'app_session_start',
  Login: 'login',
  Logout: 'logout',
  SignUpStart: 'sign_up_start',
  SignUpComplete: 'sign_up_complete',
  EmailVerificationSent: 'email_verification_sent',
  PasswordResetSent: 'password_reset_sent',
  Navigation: 'navigation',
  FeatureUsed: 'feature_used',
  BusinessProfileSaved: 'business_profile_saved',
  KnowledgeDocumentUploaded: 'knowledge_document_uploaded',
  KnowledgeDocumentDeleted: 'knowledge_document_deleted',
  KnowledgeDocumentEdited: 'knowledge_document_edited',
  BillingPortalOpened: 'billing_portal_opened',
  OnboardingStepCompleted: 'onboarding_step_completed',
  CheckoutOutcome: 'checkout_outcome',
  IntegrationConnectStarted: 'integration_connect_started',
  IntegrationActivated: 'integration_activated',
  IntegrationDisconnected: 'integration_disconnected',
  CallDetailOpened: 'call_detail_opened',
  LeadResolved: 'lead_resolved',
  NotificationResent: 'notification_resent',
  SettingsChanged: 'settings_changed',
  SurfaceError: 'surface_error',
  FullscreenEnter: 'fullscreen_enter',
  PWAInstall: 'pwa_install',
  HeatmapClick: 'heatmap_click',
  MediaPlayed: 'media_played'
} as const;

/** GA4 event param names emitted by the semantic telemetry API. */
export const TelemetryParamNames = {
  Layout: 'layout',
  InternalUserID: 'internal_user_id',
  TenantID: 'tenant_id',
  AuthMethod: 'auth_method',
  Source: 'source',
  Route: 'route',
  FeatureKey: 'feature_key',
  SettingKey: 'setting_key',
  ProfileState: 'profile_state',
  DocumentType: 'document_type',
  DocumentState: 'document_state',
  SizeBucket: 'size_bucket',
  SubscriptionStatus: 'subscription_status',
  StepKey: 'step_key',
  Outcome: 'outcome',
  Provider: 'provider',
  CallStatus: 'call_status',
  CallIntent: 'call_intent',
  DurationBucket: 'duration_bucket',
  Channel: 'channel',
  Surface: 'surface',
  MediaType: 'media_type',
  ErrorCode: 'error_code',
  CoordX: 'coord_x',
  CoordY: 'coord_y'
} as const;

/** GA4 user property names emitted by the semantic telemetry API. */
export const TelemetryUserPropertyNames = {
  Role: 'role',
  PlanTier: 'plan_tier',
  AccountAgeBucket: 'account_age_bucket',
  Region: 'region',
  EmailVerified: 'email_verified'
} as const;

/** Identity fields accepted by Telemetry.Identify. */
export interface ITelemetryIdentityDTO {
  internalUserID: string;
  tenantID?: string;
  role?: string;
  planTier?: string;
  accountCreatedAt?: Date | string;
  region?: string;
  emailVerified?: boolean;
}

/** File-local constants for Telemetry. */
const localConstants = {
  unknown: 'unknown',
  layoutPortrait: 'portrait',
  layoutLandscape: 'landscape',
  authMethodPassword: 'password',
  sourceManual: 'manual',
  sourceSignup: 'signup',
  sourceAnonymous: 'anonymous',
  sourceLogin: 'login',
  profileStateExisting: 'existing',
  profileStateNew: 'new',
  outcomeStarted: 'started',
  outcomeSuccess: 'success',
  outcomeCancel: 'cancel',
  outcomeFailed: 'failed',
  documentTypePDF: 'pdf',
  documentTypeText: 'text',
  documentTypeOther: 'other',
  eventFullscreenChange: 'fullscreenchange',
  eventAppInstalled: 'appinstalled',
  eventClick: 'click',
  mediaTypeVideo: 'video',
  mediaTypeAudio: 'audio',
  coordScale: 100,
  clickThrottleMs: 250,
  cleanupNoop: () => {},
  bucketZero: '0',
  bucketOne: '1',
  bucketCountTiny: '2_5',
  bucketCountSmall: '6_10',
  bucketCountMedium: '11_25',
  bucketCountLarge: '26_100',
  bucketCountHuge: '101_plus',
  bucketAmountFree: '0',
  bucketAmountLow: '1_49',
  bucketAmountMid: '50_99',
  bucketAmountHigh: '100_249',
  bucketAmountPremium: '250_499',
  bucketAmountEnterprise: '500_plus',
  bucketAgeWeek: '0_6',
  bucketAgeMonth: '7_29',
  bucketAgeQuarter: '30_89',
  bucketAgeHalfYear: '90_179',
  bucketAgeYear: '180_364',
  bucketAgeMature: '365_plus',
  bucketSizeTiny: '0_99kb',
  bucketSizeSmall: '100_499kb',
  bucketSizeMedium: '500kb_1mb',
  bucketSizeLarge: '1mb_5mb',
  bucketSizeHuge: '5mb_plus',
  countTinyMax: 5,
  countSmallMax: 10,
  countMediumMax: 25,
  countLargeMax: 100,
  amountLowMax: 49,
  amountMidMax: 99,
  amountHighMax: 249,
  amountPremiumMax: 499,
  accountAgeWeekMax: 6,
  accountAgeMonthMax: 29,
  accountAgeQuarterMax: 89,
  accountAgeHalfYearMax: 179,
  accountAgeYearMax: 364,
  bytesPerKilobyte: 1024,
  sizeTinyMaxKB: 99,
  sizeSmallMaxKB: 499,
  sizeMediumMaxKB: 1024,
  sizeLargeMaxKB: 5120,
  minorUnitsPerUnit: 100,
  durationSecondsPerMinute: CommonConstants.SecPerMinute,
  routeIDPattern: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  routeNumberPattern: /^\d+$/,
  routeSeparator: '/',
  routeIDPlaceholder: ':id'
} as const;

/** Cached support-traceable user id for semantic events. */
let cachedInternalUserID: string | null = null;
/** Cached tenant id for tenant-scoped semantic events. */
let cachedTenantID: string | null = null;
/** Last URL sent as a GA4 `page_view`; used to avoid duplicate events for the same location. */
let lastPageViewUrl: string | null = null;

/** @returns The low-level telemetry client. */
function client(): TelemetryClient {
  return TelemetryClient.GetInstance();
}

/** @returns Common context attached by the semantic layer. */
function base(): TelemetryParams {
  return { [TelemetryParamNames.Layout]: getLayoutTag() };
}

/** @returns Common context plus cached support-traceable user identifiers. */
function baseWithUser(): TelemetryParams {
  return {
    ...base(),
    ...(cachedInternalUserID ? { [TelemetryParamNames.InternalUserID]: cachedInternalUserID } : {}),
    ...(cachedTenantID ? { [TelemetryParamNames.TenantID]: cachedTenantID } : {})
  };
}

/** @returns The coarse current layout tag. */
function getLayoutTag(): string {
  if (typeof window === 'undefined') return localConstants.unknown;
  return window.innerWidth < window.innerHeight ? localConstants.layoutPortrait : localConstants.layoutLandscape;
}

/**
 * @param value - Free-form categorical value.
 * @returns A safe fallback for empty values.
 */
function normalizeCategory(value?: string | null): string {
  return value?.trim() || localConstants.unknown;
}

/**
 * @param route - App route string.
 * @returns A normalized route with identifier-looking segments redacted.
 */
function normalizeRoute(route: string): string {
  const segments = route
    .split(localConstants.routeSeparator)
    .filter(Boolean)
    .map((segment) => (localConstants.routeIDPattern.test(segment) || localConstants.routeNumberPattern.test(segment) ? localConstants.routeIDPlaceholder : segment));
  return `${localConstants.routeSeparator}${segments.join(localConstants.routeSeparator)}`;
}

/**
 * @param contentType - Browser file content type.
 * @returns A coarse document-type category.
 */
function bucketDocumentType(contentType?: string): string {
  const normalized = contentType?.toLowerCase() ?? '';
  if (normalized.includes(localConstants.documentTypePDF)) return localConstants.documentTypePDF;
  if (normalized.includes(localConstants.documentTypeText)) return localConstants.documentTypeText;
  return localConstants.documentTypeOther;
}

/**
 * Bucket a raw count into a GA4-safe range label.
 *
 * @param count - Raw count.
 * @returns The bucket label.
 */
function BucketCount(count: number): string {
  if (count <= 0) return localConstants.bucketZero;
  if (count === 1) return localConstants.bucketOne;
  if (count <= localConstants.countTinyMax) return localConstants.bucketCountTiny;
  if (count <= localConstants.countSmallMax) return localConstants.bucketCountSmall;
  if (count <= localConstants.countMediumMax) return localConstants.bucketCountMedium;
  if (count <= localConstants.countLargeMax) return localConstants.bucketCountLarge;
  return localConstants.bucketCountHuge;
}

/**
 * Bucket a minor-unit monetary amount into a GA4-safe major-unit range label.
 *
 * @param amountMinor - Amount in minor units.
 * @returns The bucket label.
 */
function BucketAmountMinor(amountMinor: number): string {
  const major = Math.floor(amountMinor / localConstants.minorUnitsPerUnit);
  if (major <= 0) return localConstants.bucketAmountFree;
  if (major <= localConstants.amountLowMax) return localConstants.bucketAmountLow;
  if (major <= localConstants.amountMidMax) return localConstants.bucketAmountMid;
  if (major <= localConstants.amountHighMax) return localConstants.bucketAmountHigh;
  if (major <= localConstants.amountPremiumMax) return localConstants.bucketAmountPremium;
  return localConstants.bucketAmountEnterprise;
}

/**
 * Bucket account age in days into a GA4-safe range label.
 *
 * @param days - Account age in days.
 * @returns The bucket label.
 */
function BucketAccountAgeDays(days: number): string {
  if (days <= localConstants.accountAgeWeekMax) return localConstants.bucketAgeWeek;
  if (days <= localConstants.accountAgeMonthMax) return localConstants.bucketAgeMonth;
  if (days <= localConstants.accountAgeQuarterMax) return localConstants.bucketAgeQuarter;
  if (days <= localConstants.accountAgeHalfYearMax) return localConstants.bucketAgeHalfYear;
  if (days <= localConstants.accountAgeYearMax) return localConstants.bucketAgeYear;
  return localConstants.bucketAgeMature;
}

/**
 * Bucket raw bytes into a GA4-safe range label.
 *
 * @param bytes - Raw byte size.
 * @returns The bucket label.
 */
function BucketBytes(bytes: number): string {
  const kilobytes = Math.ceil(bytes / localConstants.bytesPerKilobyte);
  if (kilobytes <= localConstants.sizeTinyMaxKB) return localConstants.bucketSizeTiny;
  if (kilobytes <= localConstants.sizeSmallMaxKB) return localConstants.bucketSizeSmall;
  if (kilobytes <= localConstants.sizeMediumMaxKB) return localConstants.bucketSizeMedium;
  if (kilobytes <= localConstants.sizeLargeMaxKB) return localConstants.bucketSizeLarge;
  return localConstants.bucketSizeHuge;
}

/**
 * @param createdAt - User creation timestamp.
 * @returns The account-age bucket, or unknown when the timestamp is invalid.
 */
function bucketAccountCreatedAt(createdAt?: Date | string): string {
  if (!createdAt) return localConstants.unknown;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return localConstants.unknown;
  const days = Math.max(0, Math.floor((Date.now() - created.getTime()) / (CommonConstants.MsPerSecond * CommonConstants.SecPerMinute * CommonConstants.MinPerHour * CommonConstants.HrPerDay)));
  return BucketAccountAgeDays(days);
}

/**
 * Cache identity and send low-cardinality user properties to GA4.
 *
 * @param identity - Safe identity metadata.
 */
function Identify(identity: ITelemetryIdentityDTO): void {
  cachedInternalUserID = identity.internalUserID;
  cachedTenantID = identity.tenantID ?? cachedTenantID;
  const properties: TelemetryParams = {
    ...(identity.role ? { [TelemetryUserPropertyNames.Role]: normalizeCategory(identity.role) } : {}),
    ...(identity.planTier ? { [TelemetryUserPropertyNames.PlanTier]: normalizeCategory(identity.planTier) } : {}),
    ...(identity.accountCreatedAt ? { [TelemetryUserPropertyNames.AccountAgeBucket]: bucketAccountCreatedAt(identity.accountCreatedAt) } : {}),
    ...(identity.region ? { [TelemetryUserPropertyNames.Region]: normalizeCategory(identity.region) } : {}),
    ...(typeof identity.emailVerified === 'boolean' ? { [TelemetryUserPropertyNames.EmailVerified]: identity.emailVerified } : {})
  };
  client().SetUser(identity.internalUserID, properties);
}

/** @param source - Session source category. */
function SessionStart(source: string = localConstants.sourceManual): void {
  client().Track(TelemetryEvents.AppSessionStart, { ...baseWithUser(), [TelemetryParamNames.Source]: source });
  PageView();
}

/** Track signup-form intent. */
function SignUpStart(): void {
  client().Track(TelemetryEvents.SignUpStart, base());
}

/** Track completed signup. */
function SignUpComplete(): void {
  client().Track(TelemetryEvents.SignUpComplete, baseWithUser());
}

/** @param method - Auth method category. */
function Login(method: string = localConstants.authMethodPassword): void {
  client().Track(TelemetryEvents.Login, { ...baseWithUser(), [TelemetryParamNames.AuthMethod]: method });
}

/** Track logout. */
function Logout(): void {
  client().Track(TelemetryEvents.Logout, baseWithUser());
}

/** @param source - Verification trigger category. */
function EmailVerificationSent(source: string = localConstants.sourceManual): void {
  client().Track(TelemetryEvents.EmailVerificationSent, { ...baseWithUser(), [TelemetryParamNames.Source]: source });
}

/** Track password reset email send. */
function PasswordResetSent(): void {
  client().Track(TelemetryEvents.PasswordResetSent, base());
}

/** @param route - App route navigated to. */
function Navigation(route: string): void {
  client().Track(TelemetryEvents.Navigation, { ...baseWithUser(), [TelemetryParamNames.Route]: normalizeRoute(route) });
  PageView();
}

/** Send a sanitized GA4 `page_view` event for the current URL, deduplicated by location. */
function PageView(): void {
  if (typeof window === 'undefined') return;
  const sanitized = client().SanitizeUrl(window.location.href);
  if (!sanitized || sanitized === lastPageViewUrl) return;
  lastPageViewUrl = sanitized;
  client().Track('page_view', { ...baseWithUser(), page_location: sanitized });
}

/** @param featureKey - Stable product feature key. */
function FeatureUsed(featureKey: string): void {
  client().Track(TelemetryEvents.FeatureUsed, { ...baseWithUser(), [TelemetryParamNames.FeatureKey]: featureKey });
}

/** @param hadExistingProfile - Whether the save updated an existing profile. */
function BusinessProfileSaved(hadExistingProfile: boolean): void {
  client().Track(TelemetryEvents.BusinessProfileSaved, {
    ...baseWithUser(),
    [TelemetryParamNames.ProfileState]: hadExistingProfile ? localConstants.profileStateExisting : localConstants.profileStateNew
  });
}

/**
 * @param contentType - Uploaded document content type.
 * @param sizeBytes - Uploaded document size.
 */
function KnowledgeDocumentUploaded(contentType: string, sizeBytes: number): void {
  client().Track(TelemetryEvents.KnowledgeDocumentUploaded, {
    ...baseWithUser(),
    [TelemetryParamNames.DocumentType]: bucketDocumentType(contentType),
    [TelemetryParamNames.SizeBucket]: BucketBytes(sizeBytes)
  });
}

/** Track knowledge document deletion. */
function KnowledgeDocumentDeleted(): void {
  client().Track(TelemetryEvents.KnowledgeDocumentDeleted, baseWithUser());
}

/** @param isNew - Whether the manual editor created a new document (false for an edit of an existing one). */
function KnowledgeDocumentEdited(isNew: boolean): void {
  client().Track(TelemetryEvents.KnowledgeDocumentEdited, {
    ...baseWithUser(),
    [TelemetryParamNames.DocumentState]: isNew ? localConstants.profileStateNew : localConstants.profileStateExisting
  });
}

/** @param stepKey - The completed onboarding step key (account/billing/phone). */
function OnboardingStepCompleted(stepKey: string): void {
  client().Track(TelemetryEvents.OnboardingStepCompleted, { ...baseWithUser(), [TelemetryParamNames.StepKey]: normalizeCategory(stepKey) });
}

/** @param outcome - The checkout outcome category (see Telemetry.Outcome). */
function CheckoutOutcome(outcome: string): void {
  client().Track(TelemetryEvents.CheckoutOutcome, { ...baseWithUser(), [TelemetryParamNames.Outcome]: normalizeCategory(outcome) });
}

/** @param provider - The provider slug whose OAuth connect handshake is starting. */
function IntegrationConnectStarted(provider: string): void {
  client().Track(TelemetryEvents.IntegrationConnectStarted, { ...baseWithUser(), [TelemetryParamNames.Provider]: normalizeCategory(provider) });
}

/** @param provider - The provider slug of the integration made the active booking target. */
function IntegrationActivated(provider?: string | null): void {
  client().Track(TelemetryEvents.IntegrationActivated, { ...baseWithUser(), [TelemetryParamNames.Provider]: normalizeCategory(provider) });
}

/** @param provider - The provider slug of the disconnected integration. */
function IntegrationDisconnected(provider?: string | null): void {
  client().Track(TelemetryEvents.IntegrationDisconnected, { ...baseWithUser(), [TelemetryParamNames.Provider]: normalizeCategory(provider) });
}

/** @param subscriptionStatus - Current subscription status category. */
function BillingPortalOpened(subscriptionStatus?: string | null): void {
  client().Track(TelemetryEvents.BillingPortalOpened, { ...baseWithUser(), [TelemetryParamNames.SubscriptionStatus]: normalizeCategory(subscriptionStatus) });
}

/**
 * @param status - Call lifecycle status.
 * @param intent - Detected call intent.
 * @param durationSeconds - Raw call duration in seconds, bucketed before send.
 */
function CallDetailOpened(status?: string | null, intent?: string | null, durationSeconds?: number | null): void {
  const durationMinutes = Math.ceil((durationSeconds ?? 0) / localConstants.durationSecondsPerMinute);
  client().Track(TelemetryEvents.CallDetailOpened, {
    ...baseWithUser(),
    [TelemetryParamNames.CallStatus]: normalizeCategory(status),
    [TelemetryParamNames.CallIntent]: normalizeCategory(intent),
    [TelemetryParamNames.DurationBucket]: BucketCount(durationMinutes)
  });
}

/** Track lead resolution. */
function LeadResolved(): void {
  client().Track(TelemetryEvents.LeadResolved, baseWithUser());
}

/** @param channel - Notification channel category. */
function NotificationResent(channel: string): void {
  client().Track(TelemetryEvents.NotificationResent, { ...baseWithUser(), [TelemetryParamNames.Channel]: normalizeCategory(channel) });
}

/** @param settingKey - Stable setting key. */
function SettingsChanged(settingKey: string): void {
  client().Track(TelemetryEvents.SettingsChanged, { ...baseWithUser(), [TelemetryParamNames.SettingKey]: settingKey });
}

/**
 * @param surface - UI surface where the error was shown.
 * @param errorCode - Stable non-sensitive error code.
 */
function SurfaceError(surface: string, errorCode?: string): void {
  client().Track(TelemetryEvents.SurfaceError, {
    ...base(),
    [TelemetryParamNames.Surface]: surface,
    // The redacted route pins the failing screen without any call-site plumbing (surfaces like the shared dashboard ErrorView render on many routes).
    ...(typeof window !== 'undefined' ? { [TelemetryParamNames.Route]: normalizeRoute(window.location.pathname) } : {}),
    ...(errorCode ? { [TelemetryParamNames.ErrorCode]: errorCode } : {})
  });
}

/**
 * Tracks a media playback start (video or audio). Emitted on the element's `play` event - reliable across browsers, unlike click-on-native-controls, which Firefox/Safari do not dispatch from the
 * UA shadow DOM. Media types come from Telemetry.MediaTypes (low cardinality by design).
 *
 * @param mediaType - The media kind (Telemetry.MediaTypes).
 * @param surface - Optional stable surface key identifying where the media lives (e.g. 'about_demo').
 */
function MediaPlayed(mediaType: string, surface?: string): void {
  client().Track(TelemetryEvents.MediaPlayed, {
    ...base(),
    [TelemetryParamNames.MediaType]: normalizeCategory(mediaType),
    ...(surface ? { [TelemetryParamNames.Surface]: surface } : {})
  });
}

/** @returns Cleanup function for passive DOM telemetry listeners. */
function RegisterPassiveSignals(): () => void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return localConstants.cleanupNoop;

  const onFullscreenChange = (): void => {
    if (document.fullscreenElement) client().Track(TelemetryEvents.FullscreenEnter, base());
  };
  const onAppInstalled = (): void => client().Track(TelemetryEvents.PWAInstall, base());

  // Capture viewport-normalized click coordinates (0-100, integers) for a downstream Data Studio heatmap (renamed from Looker Studio in April 2026), throttled and PII-free by construction
  // (no element, text, or pixel positions retained).
  let lastClickAt = 0;
  const onClick = (event: MouseEvent): void => {
    const now = Date.now();
    if (now - lastClickAt < localConstants.clickThrottleMs) return;
    lastClickAt = now;
    const width = window.innerWidth || 1;
    const height = window.innerHeight || 1;
    client().Track(TelemetryEvents.HeatmapClick, {
      ...base(),
      [TelemetryParamNames.Route]: normalizeRoute(window.location.pathname),
      [TelemetryParamNames.CoordX]: Math.round((event.clientX / width) * localConstants.coordScale),
      [TelemetryParamNames.CoordY]: Math.round((event.clientY / height) * localConstants.coordScale)
    });
  };

  document.addEventListener(localConstants.eventFullscreenChange, onFullscreenChange);
  window.addEventListener(localConstants.eventAppInstalled, onAppInstalled);
  window.addEventListener(localConstants.eventClick, onClick);

  return () => {
    document.removeEventListener(localConstants.eventFullscreenChange, onFullscreenChange);
    window.removeEventListener(localConstants.eventAppInstalled, onAppInstalled);
    window.removeEventListener(localConstants.eventClick, onClick);
  };
}

/** Public session-source categories accepted by Telemetry.SessionStart. */
export const TelemetrySources = {
  Anonymous: localConstants.sourceAnonymous,
  Login: localConstants.sourceLogin,
  Signup: localConstants.sourceSignup,
  Manual: localConstants.sourceManual
} as const;

/** Public checkout-outcome categories accepted by Telemetry.CheckoutOutcome. */
export const TelemetryOutcomes = {
  Started: localConstants.outcomeStarted,
  Success: localConstants.outcomeSuccess,
  Cancel: localConstants.outcomeCancel,
  Failed: localConstants.outcomeFailed
} as const;

/** Public media-type categories accepted by Telemetry.MediaPlayed. */
export const TelemetryMediaTypes = {
  Video: localConstants.mediaTypeVideo,
  Audio: localConstants.mediaTypeAudio
} as const;

/** Semantic one-liner telemetry API for frontend consumers. */
export const Telemetry = {
  Events: TelemetryEvents,
  Params: TelemetryParamNames,
  UserProps: TelemetryUserPropertyNames,
  Source: TelemetrySources,
  Outcome: TelemetryOutcomes,
  MediaTypes: TelemetryMediaTypes,
  Identify,
  SessionStart,
  SignUpStart,
  SignUpComplete,
  Login,
  Logout,
  EmailVerificationSent,
  PasswordResetSent,
  Navigation,
  FeatureUsed,
  BusinessProfileSaved,
  KnowledgeDocumentUploaded,
  KnowledgeDocumentDeleted,
  KnowledgeDocumentEdited,
  BillingPortalOpened,
  OnboardingStepCompleted,
  CheckoutOutcome,
  IntegrationConnectStarted,
  IntegrationActivated,
  IntegrationDisconnected,
  CallDetailOpened,
  LeadResolved,
  NotificationResent,
  SettingsChanged,
  SurfaceError,
  MediaPlayed,
  RegisterPassiveSignals,
  BucketAmountMinor,
  BucketCount,
  BucketAccountAgeDays
} as const;
